import { useState, useEffect, useCallback, useRef } from 'react';
import Map from './components/Map';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import TruckDetails from './components/TruckDetails';
import DriverManagement from './components/DriverManagement';
import DispatchPanel from './components/DispatchPanel';
import NavigationView from './components/NavigationView';
import LoginScreen from './components/LoginScreen';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import OnboardingForm from './components/OnboardingForm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyDataProvider } from './contexts/CompanyDataContext';
import { DeviceProvider } from './contexts/DeviceContext';
import type { Truck, Driver, Task, Zone } from './types';
import './App.css';

// API Base URL - always use production API for GPS data since RUT956 sends there
// Use localhost only for auth/tasks/zones which are managed locally in dev
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = import.meta.env.VITE_API_URL || (isDev ? 'http://localhost:3000' : 'https://grossistify-api.vercel.app');
// GPS data always comes from production since the RUT956 device sends data there
const GPS_API_URL = 'https://grossistify-api.vercel.app';

// Simple router to handle onboarding pages
function useSimpleRouter() {
  const [path, setPath] = useState(window.location.pathname);
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  useEffect(() => {
    const handlePopState = () => {
      setPath(window.location.pathname);
      setSearchParams(new URLSearchParams(window.location.search));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { path, searchParams };
}

function MainApp() {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [activeNavTask, setActiveNavTask] = useState<Task | null>(null);
  const [route, setRoute] = useState<{ latitude: number; longitude: number }[]>([]);
  const [sidebarOpen, _setSidebarOpen] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [_lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [zoomTrigger, setZoomTrigger] = useState(0);
  const [showDriverManagement, setShowDriverManagement] = useState(false);
  const [showDispatchPanel, setShowDispatchPanel] = useState(false);
  
  const { token } = useAuth();
  
  // Use ref to track selected truck ID to avoid race conditions
  const selectedTruckIdRef = useRef<string | null>(null);

  const fetchTrucks = useCallback(async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Always fetch GPS data from production API since RUT956 sends data there
      const response = await fetch(`${GPS_API_URL}/api/gps`, { headers });
      if (!response.ok) throw new Error('Failed to fetch trucks');
      const data = await response.json();
      
      if (data.success) {
        setTrucks(data.trucks);
        setLastUpdate(new Date());
        setError(null);
        
        // Update selected truck if it exists - use ref to avoid race condition
        const currentSelectedId = selectedTruckIdRef.current;
        if (currentSelectedId) {
          const updatedTruck = data.trucks.find((t: Truck) => t.id === currentSelectedId);
          if (updatedTruck) {
            setSelectedTruck(updatedTruck);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching trucks:', err);
      setError('Kunde inte hämta lastbilar. Kontrollera anslutningen.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchDrivers = useCallback(async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/driver`, { headers });
      if (!response.ok) return;
      const data = await response.json();
      
      if (data.success) {
        setDrivers(data.drivers || []);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  }, [token]);

  const fetchTasks = useCallback(async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/tasks`, { headers });
      if (!response.ok) return;
      const data = await response.json();
      
      if (data.success) {
        setTasks(data.tasks || []);
        
        // Update selected task if it exists
        if (selectedTask) {
          const updatedTask = data.tasks.find((t: Task) => t.id === selectedTask.id);
          if (updatedTask) {
            setSelectedTask(updatedTask);
          }
        }
        
        // Update active navigation task
        if (activeNavTask) {
          const updatedNavTask = data.tasks.find((t: Task) => t.id === activeNavTask.id);
          if (updatedNavTask) {
            setActiveNavTask(updatedNavTask);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    }
  }, [token, selectedTask?.id, activeNavTask?.id]);

  const fetchZones = useCallback(async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/zones`, { headers });
      if (!response.ok) return;
      const data = await response.json();
      
      if (data.success) {
        setZones(data.zones || []);
      }
    } catch (err) {
      console.error('Error fetching zones:', err);
    }
  }, [token]);

  const fetchRoute = useCallback(async (truckId: string) => {
    try {
      // Always fetch route data from production API since GPS data is stored there
      const response = await fetch(`${GPS_API_URL}/api/routes?truckId=${truckId}&limit=200`);
      if (!response.ok) throw new Error('Failed to fetch route');
      const data = await response.json();
      
      if (data.success) {
        setRoute(data.route);
      }
    } catch (err) {
      console.error('Error fetching route:', err);
    }
  }, []);

  useEffect(() => {
    fetchTrucks();
    fetchDrivers();
    fetchTasks();
    fetchZones();
    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      fetchTrucks();
      fetchDrivers();
      fetchTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchTrucks, fetchDrivers, fetchTasks, fetchZones]);

  useEffect(() => {
    if (selectedTruck) {
      fetchRoute(selectedTruck.id);
      const routeInterval = setInterval(() => fetchRoute(selectedTruck.id), 5000);
      return () => clearInterval(routeInterval);
    } else {
      setRoute([]);
    }
  }, [selectedTruck, fetchRoute]);

  const handleTruckSelect = (truck: Truck | null) => {
    selectedTruckIdRef.current = truck?.id ?? null;
    setSelectedTruck(truck);
    // Always trigger zoom when selecting a truck (even if same truck)
    if (truck) {
      setZoomTrigger(prev => prev + 1);
    }
  };

  const handleCloseTruckDetails = () => {
    selectedTruckIdRef.current = null;
    setSelectedTruck(null);
    setRoute([]);
  };

  const handleUpdateDriver = async (driverId: string, assignedTruck: string | null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/driver`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId, assignedTruck }),
      });
      
      if (!response.ok) throw new Error('Failed to update driver');
      
      // Refresh drivers list
      await fetchDrivers();
    } catch (err) {
      console.error('Error updating driver:', err);
    }
  };

  const handleDeleteDriver = async (driverId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/driver?driverId=${driverId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete driver');
      
      // Refresh drivers list
      await fetchDrivers();
    } catch (err) {
      console.error('Error deleting driver:', err);
    }
  };

  // ====== TASK HANDLERS ======
  
  const handleTaskCreate = async (taskData: Partial<Task>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(taskData),
      });
      
      if (!response.ok) throw new Error('Failed to create task');
      
      await fetchTasks();
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  const handleTaskAssign = async (taskId: string, driverId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId }),
      });
      
      if (!response.ok) throw new Error('Failed to assign task');
      
      await fetchTasks();
      await fetchDrivers();
    } catch (err) {
      console.error('Error assigning task:', err);
    }
  };

  const handleTaskAutoAssign = async (taskId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/auto-assign`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'Kunde inte auto-tilldela');
        return;
      }
      
      await fetchTasks();
      await fetchDrivers();
    } catch (err) {
      console.error('Error auto-assigning task:', err);
    }
  };

  const handleTaskCancel = async (taskId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to cancel task');
      
      if (selectedTask?.id === taskId) setSelectedTask(null);
      if (activeNavTask?.id === taskId) setActiveNavTask(null);
      
      await fetchTasks();
      await fetchDrivers();
    } catch (err) {
      console.error('Error cancelling task:', err);
    }
  };

  const handleTaskSelect = (task: Task | null) => {
    setSelectedTask(task);
    if (task && task.status !== 'pending' && task.status !== 'completed' && task.status !== 'cancelled') {
      // Show navigation view for active tasks
      setActiveNavTask(task);
    }
  };

  // Navigation handlers (simulating driver actions)
  const handleTaskAccept = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      await fetchTasks();
    } catch (err) {
      console.error('Error accepting task:', err);
    }
  };

  const handleTaskStart = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      await fetchTasks();
    } catch (err) {
      console.error('Error starting task:', err);
    }
  };

  const handleArrivePickup = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/arrive-pickup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      await fetchTasks();
    } catch (err) {
      console.error('Error arriving at pickup:', err);
    }
  };

  const handleLoad = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/load`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      await fetchTasks();
    } catch (err) {
      console.error('Error loading:', err);
    }
  };

  const handleArriveDropoff = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/arrive-dropoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      await fetchTasks();
    } catch (err) {
      console.error('Error arriving at dropoff:', err);
    }
  };

  const handleTaskComplete = async () => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId }),
      });
      setActiveNavTask(null);
      await fetchTasks();
      await fetchDrivers();
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleTaskFail = async (reason: string) => {
    if (!activeNavTask) return;
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${activeNavTask.id}/fail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driverId: activeNavTask.assignedDriverId, reason }),
      });
      setActiveNavTask(null);
      await fetchTasks();
      await fetchDrivers();
    } catch (err) {
      console.error('Error failing task:', err);
    }
  };

  // Count active tasks
  const activeTaskCount = tasks.filter(t => 
    !['completed', 'failed', 'cancelled', 'pending'].includes(t.status)
  ).length;

  return (
    <div className="app">
      <Header 
        truckCount={trucks.length}
        driverCount={drivers.length}
        onDriversClick={() => setShowDriverManagement(true)}
        onDispatchClick={() => setShowDispatchPanel(true)}
        activeTaskCount={activeTaskCount}
      />
      
      <div className="app-content">
        <Sidebar 
          trucks={trucks}
          selectedTruck={selectedTruck}
          onTruckSelect={handleTruckSelect}
          isOpen={sidebarOpen}
          isExpanded={sidebarExpanded}
          onExpandToggle={() => setSidebarExpanded(!sidebarExpanded)}
          loading={loading}
          error={error}
        />
        
        <main className="main-content">
          <Map 
            trucks={trucks}
            drivers={drivers}
            selectedTruck={selectedTruck}
            route={route}
            onTruckSelect={handleTruckSelect}
            sidebarOpen={sidebarOpen}
            sidebarExpanded={sidebarExpanded}
            zoomTrigger={zoomTrigger}
            zones={zones}
            tasks={tasks}
            selectedTask={selectedTask}
            onTaskSelect={handleTaskSelect}
          />
          
          {selectedTruck && (
            <TruckDetails 
              truck={selectedTruck}
              route={route}
              drivers={drivers}
              onClose={handleCloseTruckDetails}
            />
          )}
        </main>
      </div>

      {showDriverManagement && (
        <DriverManagement
          drivers={drivers}
          trucks={trucks}
          onUpdateDriver={handleUpdateDriver}
          onDeleteDriver={handleDeleteDriver}
          onClose={() => setShowDriverManagement(false)}
        />
      )}

      {showDispatchPanel && (
        <DispatchPanel
          isOpen={showDispatchPanel}
          onClose={() => setShowDispatchPanel(false)}
          tasks={tasks}
          zones={zones}
          drivers={drivers}
          trucks={trucks}
          onTaskCreate={handleTaskCreate}
          onTaskAssign={handleTaskAssign}
          onTaskAutoAssign={handleTaskAutoAssign}
          onTaskCancel={handleTaskCancel}
          onTaskSelect={handleTaskSelect}
          selectedTask={selectedTask}
        />
      )}

      {activeNavTask && (
        <NavigationView
          task={activeNavTask}
          driver={drivers.find(d => d.id === activeNavTask.assignedDriverId) || null}
          zones={zones}
          onAccept={handleTaskAccept}
          onStart={handleTaskStart}
          onArrivePickup={handleArrivePickup}
          onLoad={handleLoad}
          onArriveDropoff={handleArriveDropoff}
          onComplete={handleTaskComplete}
          onFail={handleTaskFail}
          onClose={() => setActiveNavTask(null)}
        />
      )}
    </div>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated, isSuperAdmin } = useAuth();
  const { path, searchParams } = useSimpleRouter();

  // Handle onboarding pages (public, no auth required)
  if (path.startsWith('/onboarding/')) {
    const linkId = path.split('/')[2];
    const token = searchParams.get('token') || '';
    
    if (linkId && token) {
      return <OnboardingForm linkId={linkId} token={token} />;
    }
  }

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Laddar...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // Super admin gets the super admin dashboard
  if (isSuperAdmin) {
    return <SuperAdminDashboard />;
  }

  // Regular admin gets the main app
  return <MainApp />;
}

function App() {
  return (
    <AuthProvider>
      <CompanyDataProvider>
        <DeviceProvider>
          <AppContent />
        </DeviceProvider>
      </CompanyDataProvider>
    </AuthProvider>
  );
}

export default App;
